import { Injectable } from "@angular/core";
import { environment } from "../environments/environment";

export interface BootstrapResponse {
  readonly user: { readonly pseudonymId: string } | null;
  readonly brainCells: readonly BrainCell[];
  readonly astras: readonly AstraGoal[];
  readonly actions: readonly UserAction[];
  readonly latestMood: unknown;
  readonly alarmSettings: AlarmSettings;
  readonly calendarConnection: CalendarConnection;
}

export interface BrainCell {
  readonly id: string;
  readonly label: string;
  readonly color: string;
  readonly active?: boolean;
}

export interface AstraGoal {
  readonly id: string;
  readonly brainCellId: string;
  readonly text: string;
  readonly targetDate?: string;
  readonly rank: number;
  readonly active?: boolean;
}

export interface CalendarConnection {
  readonly connected: boolean;
  readonly canConnect?: boolean;
  readonly label: string;
  readonly email?: string;
  readonly lastSyncAt?: string;
  readonly status?: "connected" | "syncing" | "error";
}

export interface AlarmSettings {
  readonly enabled: boolean;
  readonly quietHoursStart: string;
  readonly quietHoursEnd: string;
  readonly dailyCap: number;
  readonly snoozeMinutes: number;
  readonly escalationThreshold: "conservative" | "standard";
}

export type UserVisibleActionSource = "calendar" | "manual" | "ai";
export type ClassificationOrigin = "rule" | "ai";
export type CalendarOwner = "me" | "partner" | "shared";
export type RecurrenceRule = "daily" | "weekly" | "monthly" | {
  readonly frequency: "daily" | "weekly" | "monthly" | "weekdays";
  readonly interval?: number;
  readonly daysOfWeek?: readonly number[];
  readonly schoolBound?: boolean;
  readonly schoolHolidayRegion?: "NL-Zuid";
  readonly lapseDuringSchoolHolidays?: boolean;
};

export interface UserAction {
  readonly id: string;
  readonly title: string;
  readonly status: "open" | "completed" | "skipped" | "deleted";
  readonly reason: string;
  readonly lockedAt?: string;
  readonly lockedDay?: string;
  readonly dueDate?: string;
  readonly calendarStartsAt?: string;
  readonly calendarEndsAt?: string;
  readonly brainCellId?: string;
  readonly astraId?: string;
  readonly source?: UserVisibleActionSource;
  readonly classificationOrigin?: ClassificationOrigin;
  readonly calendarOwner?: CalendarOwner;
  readonly size?: "tiny" | "small" | "normal" | "medium" | "large";
  readonly nonNegotiable?: boolean;
  readonly recurrence?: RecurrenceRule;
  readonly seriesId?: string;
  readonly recurrenceInstanceDate?: string;
  readonly recurrenceOverride?: boolean;
  readonly skipCount?: number;
  readonly deferredUntil?: string;
  readonly timingLabel?: string;
}

export interface DayPlanItem {
  readonly actionId: string;
  readonly title: string;
  readonly status: "open" | "completed" | "skipped" | "deleted";
  readonly reason: string;
  readonly lockedAt?: string;
  readonly calendarStartsAt?: string;
  readonly calendarEndsAt?: string;
  readonly timingLabel?: string;
  readonly placementState?: "essentials" | "today" | "later" | "hidden" | "scheduled" | "overdue";
  readonly placementReason?: string;
  readonly brainCellId?: string;
  readonly astraId?: string;
  readonly source?: UserVisibleActionSource;
  readonly classificationOrigin?: ClassificationOrigin;
  readonly calendarOwner?: CalendarOwner;
  readonly recurrence?: RecurrenceRule;
}

export interface DayPlan {
  readonly id: string;
  readonly day: string;
  readonly items: readonly DayPlanItem[];
}

export interface TodaySetItem {
  readonly actionId: string;
  readonly isActionable: boolean;
  readonly isOverdueVisible: boolean;
  readonly isSuppressed: boolean;
  readonly suppressedReason?: "previous_day";
  readonly lockedAt?: string;
  readonly calendarStartsAt?: string;
  readonly calendarEndsAt?: string;
}

export interface TodaySet {
  readonly pseudonymId: string;
  readonly activeNow: string;
  readonly activeTimeZone: string;
  readonly ahastraDay: string;
  readonly dayStartsAt: string;
  readonly dayEndsAt: string;
  readonly items: readonly TodaySetItem[];
  readonly rankableIds: readonly string[];
  readonly overdueIds: readonly string[];
  readonly suppressedIds: readonly string[];
  readonly remainingActionableCount: number;
  readonly isDayDone: boolean;
}

export interface DayPlanResponse {
  readonly dayPlan: DayPlan;
  readonly todaySet?: TodaySet;
}

export interface DayItemsResponse {
  readonly items: readonly DayPlanItem[];
  readonly todaySet?: TodaySet;
}

export interface ActionDayPlanResponse {
  readonly action: UserAction;
  readonly dayPlan?: DayPlan;
  readonly todaySet?: TodaySet;
}

export interface CompletionResponse extends ActionDayPlanResponse {
  readonly confirmation?: string;
}

export interface CalendarImportResponse {
  readonly imported: number;
  readonly calendarConnection: CalendarConnection;
  readonly dayPlan?: DayPlan;
  readonly todaySet?: TodaySet;
}

export interface ResetTestWorldResponse {
  readonly ok: boolean;
  readonly day: string;
  readonly activeNow: string;
  readonly imported: number;
  readonly syncStatus: "imported" | "skipped" | "error";
  readonly calendarConnection: CalendarConnection;
  readonly dayPlan: DayPlan;
  readonly todaySet?: TodaySet;
  readonly actions: readonly UserAction[];
}

export interface SplitSuggestion {
  readonly originalTitle: string;
  readonly shouldSplit: boolean;
  readonly steps: readonly { readonly id: string; readonly title: string }[];
}

export interface ProposedTimeOption {
  readonly id: string;
  readonly label: string;
  readonly userTime: ActionTimingChoice;
  readonly dueDate?: string;
  readonly lockedDay?: string;
  readonly lockedAt?: string;
  readonly recurrence?: RecurrenceRule;
  readonly reason: string;
}

export interface ActionProposal {
  readonly title: string;
  readonly receipt?: string;
  readonly brainCellId?: string;
  readonly brainCellLabel: string;
  readonly astraId?: string;
  readonly astraLabel: string;
  readonly timing: string;
  readonly attention: string;
  readonly priority?: string;
  readonly recurrence?: string;
  readonly dueDate?: string;
  readonly lockedDay?: string;
  readonly lockedAt?: string;
  readonly nonNegotiable: boolean;
  readonly size: "tiny" | "small" | "normal" | "medium" | "large";
  readonly timeOptions?: readonly ProposedTimeOption[];
  readonly selectedTimeOptionId?: string;
}

export interface CreateActionOptions {
  readonly brainCellId?: string;
  readonly astraId?: string;
  readonly dueDate?: string;
  readonly lockedDay?: string;
  readonly lockedAt?: string;
  readonly nonNegotiable?: boolean;
  readonly userTime?: string;
  readonly userPriority?: string;
  readonly recurrence?: RecurrenceRule | "none";
  readonly manualTime?: string;
  readonly selectedTimeOptionId?: string;
}

export interface PriorityDebugItem {
  readonly id: string;
  readonly title: string;
  readonly score: number;
  readonly bucket: string;
  readonly chosen: boolean;
  readonly factors: readonly string[];
  readonly reason: string;
}

export type DevResetMode = "demo" | "empty";
export type ActionTimingChoice = "now" | "today" | "tonight" | "tomorrow" | "this_week" | "next_week" | "manual" | "later";
export type UsageEventType = "task_created" | "task_completed" | "task_skipped" | "task_rescheduled" | "date_selected" | "page_loaded" | "session_started" | "classification_rerun" | "developer_clock_changed";

@Injectable({ providedIn: "root" })
export class ApiClientService {
  private readonly apiBaseUrl = resolveApiBaseUrl();
  private activeNowProvider: (() => string) | null = null;

  setActiveNowProvider(provider: (() => string) | null): void {
    this.activeNowProvider = provider;
  }

  async health(): Promise<{ ok: boolean; service: string }> {
    return this.get("/health");
  }

  async createInstallation(installSeed: string): Promise<{ user: { pseudonymId: string } }> {
    return this.post("/v1/installations", { installSeed, language: "nl" });
  }

  async saveOnboarding(pseudonymId: string): Promise<unknown> {
    const brainCells = [
      { id: "cell_personal", label: "Persoonlijk", color: "personal" },
      { id: "cell_admin", label: "Administratie", color: "admin" },
      { id: "cell_house", label: "Huishouden", color: "house" },
      { id: "cell_health", label: "Gezondheid", color: "health" },
      { id: "cell_work", label: "Werk", color: "work" },
    ];
    return this.post("/v1/onboarding", {
      pseudonymId,
      brainCells,
      astras: [],
      strictness: "gentle",
    });
  }

  async saveMood(pseudonymId: string, mood: string, capacity: string): Promise<unknown> {
    return this.post("/v1/mood", { pseudonymId, mood, capacity, day: today() });
  }

  async proposeAction(pseudonymId: string, title: string, userTime?: ActionTimingChoice, userPriority?: "low" | "medium" | "high", recurrence?: RecurrenceRule | "none", dueDate?: string, manualTime?: string): Promise<{ proposal: ActionProposal }> {
    return this.post("/v1/actions/proposal", { pseudonymId, title, userTime, userPriority, recurrence, dueDate, manualTime });
  }

  async createAction(pseudonymId: string, title: string, size: string, proposal?: CreateActionOptions): Promise<{ action: UserAction; suggestion: SplitSuggestion }> {
    return this.post("/v1/actions", { pseudonymId, title, size, ...proposal });
  }

  async createAstra(pseudonymId: string, text: string, targetDate: string, brainCellId?: string): Promise<{ astra: AstraGoal }> {
    return this.post("/v1/astras", { pseudonymId, text, targetDate, brainCellId });
  }

  async assignActionAstra(pseudonymId: string, actionId: string, astraId?: string): Promise<ActionDayPlanResponse> {
    return this.post(`/v1/actions/${encodeURIComponent(actionId)}/astra`, { pseudonymId, astraId });
  }

  async rebuildDayPlan(pseudonymId: string, day = today()): Promise<DayPlanResponse> {
    return this.post(`/v1/day-plan/${encodeURIComponent(pseudonymId)}/${day}/rebuild`, {});
  }

  async rebuildOverloadDayPlan(pseudonymId: string, day = today()): Promise<{ dayPlan: DayPlan }> {
    return this.post(`/v1/day-plan/${encodeURIComponent(pseudonymId)}/${day}/overload`, {});
  }

  async safeReturnDayPlan(pseudonymId: string, daysAway: 3 | 7 | 14, day = today()): Promise<{ dayPlan: DayPlan }> {
    return this.post(`/v1/day-plan/${encodeURIComponent(pseudonymId)}/${day}/safe-return`, { daysAway });
  }

  async priorityDebug(pseudonymId: string, day = today()): Promise<{ items: readonly PriorityDebugItem[] }> {
    return this.get(`/v1/day-plan/${encodeURIComponent(pseudonymId)}/${day}/debug`);
  }

  async daySet(pseudonymId: string, day = today()): Promise<DayItemsResponse> {
    return this.get(`/v1/day-plan/${encodeURIComponent(pseudonymId)}/${day}/day-set`);
  }

  async dayOverview(pseudonymId: string, day = today()): Promise<DayItemsResponse> {
    return this.get(`/v1/day-plan/${encodeURIComponent(pseudonymId)}/${day}/overview`);
  }

  async rerunClassification(pseudonymId: string): Promise<{ ok: boolean; actions: readonly UserAction[] }> {
    return this.post(`/v1/dev/${encodeURIComponent(pseudonymId)}/reclassify`, { developerMode: true });
  }

  async completeAction(pseudonymId: string, actionId: string): Promise<CompletionResponse> {
    return this.post(`/v1/actions/${encodeURIComponent(actionId)}/complete`, { pseudonymId });
  }

  async replanAction(pseudonymId: string, actionId: string, userTime: ActionTimingChoice, userPriority: "low" | "medium" | "high"): Promise<ActionDayPlanResponse> {
    return this.post(`/v1/actions/${encodeURIComponent(actionId)}/replan`, { pseudonymId, userTime, userPriority });
  }

  async skipAction(pseudonymId: string, actionId: string, replanFor: "tomorrow" | "later" | "full" = "tomorrow", day = today()): Promise<ActionDayPlanResponse> {
    return this.post(`/v1/actions/${encodeURIComponent(actionId)}/skip`, { pseudonymId, mood: "oke", day, replanFor });
  }

  async removeAction(pseudonymId: string, actionId: string, scope: "occurrence" | "series" = "occurrence"): Promise<ActionDayPlanResponse> {
    return this.post(`/v1/actions/${encodeURIComponent(actionId)}/remove`, { pseudonymId, scope });
  }

  async makeSmaller(pseudonymId: string, actionId: string): Promise<{ action?: UserAction; suggestion: SplitSuggestion }> {
    return this.post(`/v1/actions/${encodeURIComponent(actionId)}/make-smaller`, { pseudonymId, apply: true });
  }

  async getAlarmSettings(pseudonymId: string): Promise<{ alarmSettings: AlarmSettings }> {
    return this.get(`/v1/alarm-settings/${encodeURIComponent(pseudonymId)}`);
  }

  async saveAlarmSettings(pseudonymId: string, settings: Partial<AlarmSettings>): Promise<{ alarmSettings: AlarmSettings }> {
    return this.put(`/v1/alarm-settings/${encodeURIComponent(pseudonymId)}`, settings);
  }

  googleCalendarStartUrl(pseudonymId: string): string {
    return `${this.apiBaseUrl}/v1/calendar/google/start?pseudonymId=${encodeURIComponent(pseudonymId)}`;
  }

  async getCalendarConnection(pseudonymId: string): Promise<{ calendarConnection: CalendarConnection }> {
    return this.get(`/v1/calendar-connection/${encodeURIComponent(pseudonymId)}`);
  }

  async importCalendarEvents(pseudonymId: string, day = today()): Promise<CalendarImportResponse> {
    return this.post(`/v1/calendar-events/${encodeURIComponent(pseudonymId)}/import`, { day });
  }

  async resetTestWorld(pseudonymId: string): Promise<ResetTestWorldResponse> {
    return this.post(`/v1/dev/${encodeURIComponent(pseudonymId)}/reset-test-world`, { developerMode: true });
  }

  async disconnectCalendar(pseudonymId: string): Promise<{ calendarConnection: CalendarConnection }> {
    return this.post(`/v1/calendar-connection/${encodeURIComponent(pseudonymId)}/disconnect`, {});
  }

  async bootstrap(pseudonymId: string): Promise<BootstrapResponse> {
    return this.get(`/v1/bootstrap/${encodeURIComponent(pseudonymId)}`);
  }

  async resetDevData(mode: DevResetMode, installSeed: string, day: string): Promise<{ ok: boolean; mode: DevResetMode; store: string; user: { readonly pseudonymId: string } | null; dayPlan: DayPlan | null }> {
    return this.post("/v1/dev/reset", { mode, installSeed, day });
  }

  async recordUsageEvent(pseudonymId: string, type: UsageEventType, payload: Record<string, unknown> = {}): Promise<{ ok: boolean }> {
    return this.post("/v1/usage-events", { pseudonymId, type, payload });
  }

  private async get<T>(path: string): Promise<T> {
    const response = await fetch(`${this.apiBaseUrl}${this.withActiveQuery(path)}`);
    return this.read<T>(response);
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(`${this.apiBaseUrl}${path}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(this.withSession(body)),
    });
    return this.read<T>(response);
  }

  private async put<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(`${this.apiBaseUrl}${path}`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(this.withSession(body)),
    });
    return this.read<T>(response);
  }

  private withSession(body: unknown): unknown {
    if (!isRecord(body)) return body;
    const activeNow = this.activeNowProvider?.();
    return {
      ...body,
      ...(typeof body.sessionId === "string" ? {} : { sessionId: this.sessionId() }),
      ...(typeof body.activeNow === "string" || !activeNow ? {} : { activeNow }),
      ...(typeof body.activeTimeZone === "string" ? {} : { activeTimeZone: activeTimeZone() }),
    };
  }

  private withActiveQuery(path: string): string {
    const activeNow = this.activeNowProvider?.();
    if (!activeNow) return path;
    const separator = path.includes("?") ? "&" : "?";
    return `${path}${separator}activeNow=${encodeURIComponent(activeNow)}&activeTimeZone=${encodeURIComponent(activeTimeZone())}`;
  }

  private sessionId(): string {
    const key = "ahastra.session.id";
    const existing = globalThis.sessionStorage?.getItem(key);
    if (existing) return existing;
    const generated = globalThis.crypto?.randomUUID?.() ?? `session-${Date.now()}`;
    globalThis.sessionStorage?.setItem(key, generated);
    return generated;
  }

  private async read<T>(response: Response): Promise<T> {
    if (!response.ok) {
      throw new Error((await responseErrorMessage(response)) ?? `Ahastra API request failed: ${response.status}`);
    }
    return response.json() as Promise<T>;
  }
}

async function responseErrorMessage(response: Response): Promise<string | undefined> {
  const text = await response.text().catch(() => "");
  if (!text) return undefined;
  try {
    const parsed = JSON.parse(text) as unknown;
    if (isRecord(parsed) && typeof parsed.error === "string" && parsed.error.trim()) return parsed.error.trim();
    if (isRecord(parsed) && typeof parsed.message === "string" && parsed.message.trim()) return parsed.message.trim();
  } catch {
    const clean = text.trim();
    if (clean) return clean.slice(0, 240);
  }
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function resolveApiBaseUrl(): string {
  const browserLocation = globalThis.location;
  if (browserLocation?.protocol === "http:" && browserLocation.hostname && isKnownLocalDevClientPort(browserLocation.port)) {
    const host = browserLocation.hostname.includes(":") ? `[${browserLocation.hostname}]` : browserLocation.hostname;
    return `http://${host}:4317`;
  }
  if ((browserLocation?.protocol === "http:" || browserLocation?.protocol === "https:") && browserLocation.origin) return browserLocation.origin;
  return environment.apiBaseUrl;
}

function isKnownLocalDevClientPort(port: string): boolean {
  const parsed = Number(port);
  return port === "4173" || port === "8100" || (Number.isInteger(parsed) && parsed >= 4200 && parsed <= 4300);
}

function today(): string {
  const value = new Date();
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const date = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${date}`;
}

function activeTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Amsterdam";
}
